import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { PodcastData } from '../types/podcast';
import { useAudio } from '../contexts/AudioContext';
import { useEffect } from 'react';
import { KeyTopics } from './insights/KeyTopics';
import { KeyMoments } from './insights/KeyMoments';
import { PerspectivesSection } from './insights/PerspectivesSection';
import { SidebarInsights } from './insights/SidebarInsights';
import { EnhancedSummary } from './insights/EnhancedSummary';
import { AdditionalInsights } from './insights/AdditionalInsights';
import { CompanyPerspectives } from './insights/CompanyPerspectives';
import { SpeakerDetails } from './insights/SpeakerDetails';
import { MainAudioPlayer } from './MainAudioPlayer';

interface PodcastInsightsProps {
  data: PodcastData;
  audioUrl: string;
  episodeTitle: string;
  podcastName: string;
  podcastImage?: string;
  publishedDate?: string;
}

export const PodcastInsights = ({ 
  data, 
  audioUrl, 
  episodeTitle, 
  podcastName,
  podcastImage,
  publishedDate
}: PodcastInsightsProps) => {
  const { setAudioUrl } = useAudio();

  // Set the audio URL when the component mounts or when it changes
  useEffect(() => {
    setAudioUrl(audioUrl);
    // Cleanup when unmounting
    return () => setAudioUrl('');
  }, [audioUrl, setAudioUrl]);

  // Function to format the date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Ensure data properties exist with default values
  const {
    speakers = {},
    speaker_details = [],
    summary = '',
    breakdown = '',
    mood = '',
    key_topics = [],
    most_interesting_moments = [],
    topic_perspectives = [],
    sector_perspectives = [],
    company_perspectives = [],
    companies_mentioned = [],
    people_mentioned = [],
    predictions = [],
    actionable_insights = [],
    contrarian_views = [],
    under_the_radar = [],
    risks = [],
    memorable_quotes = [],
    macro = [],
    enhanced_summary = {
      summary: '',
      key_value_prop: '',
      should_listen: undefined,
      time_to_value_timestamps: undefined
    }
  } = data || {};

  // Process people for TagCloud display
  const processedPeople = (people_mentioned || []).map(person => {
    if (typeof person === 'string') {
      // Check for the new format with '--' separator
      if (person.includes('--')) {
        const [name, description] = person.split('--');
        return description ? { name: name.trim(), description: description.trim() } : name;
      }
      // Check for the format with ' - ' separator
      else if (person.includes(' - ')) {
        const [name, description] = person.split(' - ');
        return description ? { name: name.trim(), description: description.trim() } : name;
      }
      // Check for the format with ': ' separator
      else if (person.includes(': ')) {
        const [name, description] = person.split(': ');
        return description ? { name, description } : name;
      }
      // Check for description in parentheses
      else {
        const match = person.match(/^(.*?)\s*\((.*?)\)\s*$/);
        if (match) {
          const [, name, description] = match;
          return { name: name.trim(), description: description.trim() };
        }
      }
    }
    return person;
  });

  // Process companies for TagCloud display - using the same logic as people
  const processedCompanies = (companies_mentioned || []).map(company => {
    if (typeof company === 'string') {
      // Check for the new format with '--' separator
      if (company.includes('--')) {
        const [name, description] = company.split('--');
        return description ? { name: name.trim(), description: description.trim() } : name;
      }
      // Check for the format with ' - ' separator
      else if (company.includes(' - ')) {
        const [name, description] = company.split(' - ');
        return description ? { name: name.trim(), description: description.trim() } : name;
      }
      // Check for the format with ': ' separator
      else if (company.includes(': ')) {
        const [name, description] = company.split(': ');
        return description ? { name, description } : name;
      }
      // Check for description in parentheses
      else {
        const match = company.match(/^(.*?)\s*\((.*?)\)\s*$/);
        if (match) {
          const [, name, description] = match;
          return { name: name.trim(), description: description.trim() };
        }
      }
    }
    return company;
  });

  return (
    <>
      <Container maxW="container.xl" py={8} pb={32} data-testid="podcast-insights">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="lg">{episodeTitle}</Heading>
            <Text color="gray.600" fontSize="lg" fontWeight="medium" mt={1}>
              {podcastName}
            </Text>
            {publishedDate && (
              <Text color="gray.500" fontSize="md" mt={1}>
                {formatDate(publishedDate)}
              </Text>
            )}
            <Text color="gray.600" mt={2}>
              {Object.values(speakers).join(', ')}
            </Text>
          </Box>

          {/* Main Content */}
          <VStack spacing={6} align="stretch">
            {/* Main Content Grid */}
            <Grid
              templateColumns={{ base: "1fr", lg: "3fr 1fr" }}
              gap={6}
            >
              {/* Left Column */}
              <GridItem>
                <VStack spacing={6} align="stretch">
                  {/* Key Topics */}
                  {key_topics && key_topics.length > 0 && (
                    <KeyTopics topics={key_topics} />
                  )}

                  {/* Enhanced Summary */}
                  <EnhancedSummary
                    summary={enhanced_summary?.summary || summary}
                    keyValueProp={enhanced_summary?.key_value_prop}
                    shouldListen={enhanced_summary?.should_listen}
                    timeToValueTimestamps={enhanced_summary?.time_to_value_timestamps}
                    mood={mood}
                  />

                  {/* Key Moments */}
                  {most_interesting_moments && most_interesting_moments.length > 0 && (
                    <KeyMoments moments={most_interesting_moments} />
                  )}

                  {/* Perspectives */}
                  {(topic_perspectives.length > 0 || sector_perspectives.length > 0) && (
                    <PerspectivesSection
                      topicPerspectives={topic_perspectives}
                      sectorPerspectives={sector_perspectives}
                    />
                  )}

                  {/* Company Perspectives */}
                  {company_perspectives && company_perspectives.length > 0 && (
                    <CompanyPerspectives perspectives={company_perspectives} />
                  )}

                  {/* Additional Insights */}
                  <AdditionalInsights
                    breakdown={breakdown}
                    predictions={predictions}
                    contrarianViews={contrarian_views}
                    memorableQuotes={memorable_quotes}
                    macro={macro}
                  />
                </VStack>
              </GridItem>

              {/* Right Column */}
              <GridItem>
                <SidebarInsights
                  companies={processedCompanies}
                  people={processedPeople}
                  predictions={predictions}
                  actionableInsights={actionable_insights}
                  risks={risks}
                  underTheRadar={under_the_radar}
                />
              </GridItem>
            </Grid>

            {speaker_details && speaker_details.length > 0 && (
              <Box mt={8}>
                <Heading size="md" mb={4}>Speakers</Heading>
                <SpeakerDetails speakers={speaker_details} />
              </Box>
            )}
          </VStack>
        </VStack>
      </Container>
      <MainAudioPlayer 
        metadata={{
          title: episodeTitle,
          artist: podcastName,
          album: podcastName,
          artwork: podcastImage
        }} 
      />
    </>
  );
}; 